// auth.actions.ts

import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { AppwriteException, Models } from 'appwrite';
import { User } from 'src/app/models';

/**
 * Session Management Actions
 * Handles initialization and session lifecycle
 */
export const SessionActions = createActionGroup({
  source: 'Auth/Session',
  events: {
    'Initialize': emptyProps(),
    'Initialize Success': props<{
      token: string;
      learmoProfile: User;
      appwriteProfile: Models.User<Models.Preferences>;
    }>(),
    'Initialize Failure': emptyProps(),
    'Forget': props<{ redirect?: boolean }>(),
    'Clear Storage': emptyProps(),
  },
});

/**
 * Login Actions
 * Handles all login-related operations
 */
export const LoginActions = createActionGroup({
  source: 'Auth/Login',
  events: {
    'Email Password': props<{
      email: string;
      password: string;
      fromRegister?: boolean;
    }>(),
    'Magic Link': props<{ email: string }>(),
    'Phone': props<{ phone: string }>(),
    'OAuth': props<{ provider: string }>(),
    'Success': emptyProps(),
    'Failure': props<{ error: AppwriteException | Error }>(),
  },
});

/**
 * Token Management Actions
 * Handles JWT token operations
 */
export const TokenActions = createActionGroup({
  source: 'Auth/Token',
  events: {
    'Generate': emptyProps(),
    'Set': props<{ token: string }>(),
    'Refresh': emptyProps(),
    'Refresh Success': props<{ token: string }>(),
    'Refresh Failure': props<{ error: AppwriteException | Error }>(),
    'Schedule Refresh': props<{ timeout: number }>(),
    'Cancel Scheduled Refresh': emptyProps(),
  },
});

/**
 * User Profile Actions
 * Handles fetching and updating user profiles
 */
export const UserProfileActions = createActionGroup({
  source: 'Auth/Profile',
  events: {
    'Load Learmo Profile': props<{ redirect?: boolean }>(),
    'Load Appwrite Profile': emptyProps(),
    'Set Learmo Profile': props<{ learmoProfile: User }>(),
    'Set Appwrite Profile': props<{
      appwriteProfile: Models.User<Models.Preferences>;
    }>(),
    'Update Profile': props<{ profile: Partial<User> }>(),
    'Update Profile Success': props<{ profile: User }>(),
    'Update Profile Failure': props<{ error: Error }>(),
  },
});

/**
 * Registration Webhook Actions
 * Handles post-registration webhook validation
 */
export const WebhookActions = createActionGroup({
  source: 'Auth/Webhook',
  events: {
    'Check': emptyProps(),
    'Poll': emptyProps(),
    'Complete': emptyProps(),
    'Timeout': emptyProps(),
  },
});

/**
 * Onboarding Actions
 * Handles user onboarding flow
 */
export const OnboardingActions = createActionGroup({
  source: 'Auth/Onboarding',
  events: {
    'Mark Website Setup Done': emptyProps(),
    'Mark Website Setup Not Done': emptyProps(),
    'Update Success': props<{ isComplete: boolean }>(),
    'Update Failure': props<{ error: Error }>(),
  },
});

/**
 * Registration Actions
 * Handles new user registration
 */
export const RegistrationActions = createActionGroup({
  source: 'Auth/Registration',
  events: {
    'Register': props<{
      firstname: string;
      lastname: string;
      email: string;
      password: string;
      trackingCode?: string;
    }>(),
    'Register Success': props<{ email: string; password: string }>(),
    'Register Failure': props<{ error: AppwriteException | Error }>(),
    'Send Verification Email': emptyProps(),
    'Verification Email Sent': props<{ result: any }>(),
    'Verification Email Failed': props<{ error: Error }>(),
  },
});

/**
 * Password Recovery Actions
 * Handles password reset flow
 */
export const PasswordRecoveryActions = createActionGroup({
  source: 'Auth/Password Recovery',
  events: {
    'Request Reset': props<{ email: string }>(),
    'Request Reset Success': emptyProps(),
    'Request Reset Failure': props<{ error: Error }>(),
    'Confirm Reset': props<{
      userId: string;
      secret: string;
      password: string;
    }>(),
    'Confirm Reset Success': emptyProps(),
    'Confirm Reset Failure': props<{ error: Error }>(),
  },
});